import { GridColDef } from '@mui/x-data-grid';
import { Box, CircularProgress, Typography } from '@mui/material';
import { IResourceAllocationDataMemberGridColumns } from '../type';
import { ViewType } from '../../common/utils/enum';
import { TableVirtuoso } from 'react-virtuoso';
import NoRecords from '../../common/application/no-records';
import { statusMap } from '../../common/utils/helperFunctions';

const cellTypoStyle = {
  fontFamily: 'Be Vietnam Pro',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  textAlign: 'left',
  color: '#1A1B1F',
  maxWidth: '220px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

interface ITableData {
  updateResourceAllocationDataMemberCallback: () => Promise<void>;
  columns: GridColDef[];
  isLoading?: boolean;
  view?: ViewType;
  data: IResourceAllocationDataMemberGridColumns[];
}

const ResourceAllocationDataTable = ({
  columns,
  isLoading,
  data
}: ITableData): JSX.Element => {
  const columnsInfo: Array<{
    headerName: string | undefined;
    width: number | undefined;
  }> = columns.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    width: column.width
  }));
  const estimatedRowHeight = 50;
  const tableHeight = (data.length + 1) * estimatedRowHeight;

  return (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
            height: 'calc(100vh - 700px)'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {data?.length === 0 ? (
            <NoRecords message={'No Resource Allocations Available'} />
          ) : (
            <TableVirtuoso
              scrolling="smooth"
              style={{ height: `${tableHeight}px`, overflow: 'hidden' }}
              data={data}
              fixedHeaderContent={() => (
                <tr style={{ background: '#FFFFFF' }}>
                  {columnsInfo.map((column, index) => (
                    <th
                      key={index}
                      style={{
                        fontFamily: 'Be Vietnam Pro',
                        fontSize: '15px',
                        fontWeight: '500',
                        lineHeight: '16px',
                        letterSpacing: '0.5px',
                        color: '#46464F',
                        padding: '12px 24px',
                        textAlign: 'left',
                        backgroundColor: '#E5F2FF',
                        width: 900
                      }}
                    >
                      {column.headerName}
                    </th>
                  ))}
                </tr>
              )}
              itemContent={(
                _index: number,
                item: IResourceAllocationDataMemberGridColumns
              ) => {
                return (
                  <>
                    <td
                      style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            display: 'inline-block',
                            backgroundColor: item.project.colorCode,
                            marginRight: 1,
                            verticalAlign: 'middle'
                          }}
                        />
                        <Typography sx={cellTypoStyle}>
                          {item.project.projectName}
                        </Typography>
                      </Box>
                    </td>
                    <td
                      style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            display: 'inline-block',
                            backgroundColor: statusMap(
                              item.project.projectStatus
                            ).colorCode,
                            marginRight: 1,
                            verticalAlign: 'middle'
                          }}
                        />
                        <Typography sx={cellTypoStyle}>
                          {statusMap(item.project.projectStatus).status}
                        </Typography>
                      </Box>
                    </td>
                    <td
                      style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      {item.hoursPerWeek}{' '}
                    </td>
                    <td
                      style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      {' '}
                      {item.roleStartDate
                        ? new Date(item.roleStartDate).toLocaleDateString(
                            'en-US',
                            {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric'
                            }
                          )
                        : ''}{' '}
                    </td>
                    <td
                      style={{
                        padding: '12px 24px',
                        borderBottom: '1px solid #F2F0F4'
                      }}
                    >
                      {' '}
                      {item.roleEndDate
                        ? new Date(item.roleEndDate).toLocaleDateString(
                            'en-US',
                            {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric'
                            }
                          )
                        : ''}{' '}
                    </td>
                  </>
                );
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ResourceAllocationDataTable;
